import {gql} from '@apollo/client';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {CanvasCore} from '../BenchlingAppCanvas';
import {GetCanvasById, GetCanvasByIdVariables} from './__generated/GetCanvasById';
import CanvasCreate from './CanvasCreate';
import DefaultCanvasPlaceholder, {
  InacessibleCanvasPlaceholder,
  RemovedFeaturePlaceholder,
} from './DefaultPlaceholder';
import GenericCanvasShell from './GenericCanvasShell';

type BenchlingAppCanvas = ExtractByTypename<
  GetCanvasById['benchlingAppCanvasById'],
  'BenchlingAppCanvasNode'
>;
type BenchlingAppCanvasOrInaccessible = GetCanvasById['benchlingAppCanvasById'];

export const BATCHED_CANVAS_QUERY = gql`
  query GetCanvasById($canvasId: StrictString!) @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppCanvasById(canvasId: $canvasId) {
      ...BenchlingAppCanvasGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppCanvas').fragment}
`;

const isAccessibleCanvas = (canvas: BenchlingAppCanvasOrInaccessible): canvas is BenchlingAppCanvas => {
  if (isInaccessible(canvas)) {
    return false;
  }
  return true;
};

const isCanvasWithBlocks = (canvas: BenchlingAppCanvas): boolean => {
  if (canvas.uiBlocks.length === 0) {
    return false;
  }
  return true;
};

export interface GenericCanvasContainerProps {
  featureUserDefinedId: string;
  appInstallationId: string;
  resourceId: string;
  canvasId?: string;
  canCreateCanvas: boolean;
  doCreateCanvas: (
    featureUserDefinedId: string,
    appInstallationId: string,
    resourceId: string
  ) => Promise<void>;
  forceDisable: boolean;
}

const GenericCanvasContainer = (props: GenericCanvasContainerProps): JSX.Element => {
  if (props.canvasId) {
    return <ExistingCanvas canvasId={props.canvasId} forceDisable={props.forceDisable} />;
  } else {
    return <CanvasCreate {...props} doCreateCanvas={props.doCreateCanvas} />;
  }
};

const ExistingCanvas = (props: {canvasId: string; forceDisable: boolean}): JSX.Element => {
  const [canvas, updateCanvas] = useState<BenchlingAppCanvasOrInaccessible>();
  const onQueryComplete = (data: GetCanvasById): void => {
    updateCanvas(data.benchlingAppCanvasById);
  };

  const batchedPollingQuery = useBenchlingQuery<GetCanvasById, GetCanvasByIdVariables>(BATCHED_CANVAS_QUERY, {
    variables: {canvasId: props.canvasId},
    pollInterval: BenchlingConfig.BENCHLING_APP_CANVAS_POLLING_FREQUENCY_MS,
    renderCachedDataWhenRefetching: true,
    onCompleted: onQueryComplete,
  });
  if (batchedPollingQuery.type === QueryResultType.PENDING || !canvas) {
    return (
      <GenericCanvasShell appName="Loading..." featureName="Loading...">
        <DefaultCanvasPlaceholder />
      </GenericCanvasShell>
    );
  }
  if (!isAccessibleCanvas(canvas)) {
    return (
      <GenericCanvasShell appName="Unknown App" featureName="Unknown Feature" isInaccessible={true}>
        <InacessibleCanvasPlaceholder />
      </GenericCanvasShell>
    );
  }

  const featureName = canvas.featureOrNull?.name || 'App Feature Not Found';
  const isFeatureMissing = !canvas.featureOrNull;
  const disableCanvas = props.forceDisable || isFeatureMissing;

  if (!isCanvasWithBlocks(canvas)) {
    if (isFeatureMissing) {
      return (
        <GenericCanvasShell
          appName={canvas.benchlingApp.name}
          featureName={featureName}
          canvasSession={canvas.session}
          canvasId={canvas.id}
          isFeatureMissing={isFeatureMissing}
        >
          <RemovedFeaturePlaceholder />
        </GenericCanvasShell>
      );
    }
    return (
      <GenericCanvasShell
        appName={canvas.benchlingApp.name}
        featureName={featureName}
        canvasSession={canvas.session}
        canvasId={canvas.id}
        isFeatureMissing={isFeatureMissing}
      >
        <DefaultCanvasPlaceholder />
      </GenericCanvasShell>
    );
  }

  return (
    <GenericCanvasShell
      appName={canvas.benchlingApp.name}
      featureName={featureName}
      canvasSession={canvas.session}
      canvasId={canvas.id}
      dataTestAttrs={{component: 'GenericCanvasShell', element: 'root'}}
      isFeatureMissing={isFeatureMissing}
    >
      <CanvasCore
        canvas={canvas}
        enabledOverride={!disableCanvas}
        refetchCanvases={async () => {}}
        dataTestAttrs={{component: 'Canvas', element: 'root'}}
      />
    </GenericCanvasShell>
  );
};

export default GenericCanvasContainer;
